<script>
import {
  createShippingRemoteArea,
  getShippingRemoteAreas,
  updateShippingRemoteArea,
} from '@/api/product.js'
import AppPagination from '@/components/common/AppPagination.vue'

const emptyForm = () => ({
  regionType: 'REMOTE',
  areaNm: '',
  zipCode: '',
  zipFrom: '',
  zipTo: '',
  surchargeAmount: null,
  activeYn: 'Y',
})

export default {
  name: 'ShippingRemoteAreaPage',
  components: { AppPagination },
  data() {
    return {
      search: {
        keyword: '',
        regionType: '',
        activeYn: '',
      },
      regionOptions: [
        { title: '전체', value: '' },
        { title: '제주', value: 'JEJU' },
        { title: '도서산간', value: 'REMOTE' },
      ],
      activeOptions: [
        { title: '전체', value: '' },
        { title: '사용', value: 'Y' },
        { title: '미사용', value: 'N' },
      ],
      items: [],
      total: 0,
      page: 1,
      size: 10,
      loading: false,
      dialog: false,
      saving: false,
      editTarget: null,
      form: emptyForm(),
    }
  },
  computed: {
    formTitle() {
      return this.editTarget ? '도서산간 지역 수정' : '도서산간 지역 등록'
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getShippingRemoteAreas({
          keyword: this.search.keyword || undefined,
          regionType: this.search.regionType || undefined,
          activeYn: this.search.activeYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('[ShippingRemoteAreaPage] fetchList error:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', regionType: '', activeYn: '' }
      this.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
    openCreateDialog() {
      this.editTarget = null
      this.form = emptyForm()
      this.dialog = true
    },
    openEditDialog(item) {
      this.editTarget = item
      this.form = {
        regionType: item.regionType || 'REMOTE',
        areaNm: item.areaNm || '',
        zipCode: item.zipCode || '',
        zipFrom: item.zipFrom || '',
        zipTo: item.zipTo || '',
        surchargeAmount: item.surchargeAmount === null || item.surchargeAmount === undefined ? null : Number(item.surchargeAmount),
        activeYn: item.activeYn || 'Y',
      }
      this.dialog = true
    },
    validateForm() {
      if (!this.form.areaNm) return '지역명을 입력해 주세요.'
      if (!this.form.zipCode && (!this.form.zipFrom || !this.form.zipTo)) {
        return '단일 우편번호 또는 우편번호 범위를 입력해 주세요.'
      }
      if (this.form.zipFrom && this.form.zipTo && this.form.zipTo < this.form.zipFrom) {
        return '우편번호 범위의 종료값은 시작값보다 작을 수 없습니다.'
      }
      if (this.form.surchargeAmount !== null && this.form.surchargeAmount !== '' && Number(this.form.surchargeAmount) < 0) {
        return '추가 배송비는 0 이상이어야 합니다.'
      }
      return ''
    },
    async saveRemoteArea() {
      const message = this.validateForm()
      if (message) {
        alert(message)
        return
      }
      this.saving = true
      const payload = {
        ...this.form,
        zipCode: this.form.zipCode || null,
        zipFrom: this.form.zipFrom || null,
        zipTo: this.form.zipTo || null,
        surchargeAmount: this.form.surchargeAmount === '' || this.form.surchargeAmount === null
          ? null
          : Number(this.form.surchargeAmount),
      }
      try {
        const res = this.editTarget
          ? await updateShippingRemoteArea(this.editTarget.shippingRemoteAreaZipCode, payload)
          : await createShippingRemoteArea(payload)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        } else {
          alert(res.data?.message || '도서산간 지역 저장에 실패했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '도서산간 지역 저장 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },
    regionLabel(value) {
      return value === 'JEJU' ? '제주' : '도서산간'
    },
    activeLabel(value) {
      return value === 'Y' ? '사용' : '미사용'
    },
    zipLabel(item) {
      if (item.zipCode) return item.zipCode
      return `${item.zipFrom || '-'} ~ ${item.zipTo || '-'}`
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '정책 기본값'
      return `${Number(value || 0).toLocaleString('ko-KR')}원`
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">도서산간 지역 관리</div>
        <div class="text-body-2 text-grey mt-1">배송지 우편번호를 기준으로 제주/도서산간 여부를 판정하는 기준을 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">지역 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <div class="remote-filter">
          <v-text-field
            v-model="search.keyword"
            label="검색"
            placeholder="지역명, 우편번호"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @keyup.enter="handleSearch"
          />
          <v-select v-model="search.regionType" :items="regionOptions" label="지역 구분" variant="outlined" density="compact" hide-details />
          <v-select v-model="search.activeYn" :items="activeOptions" label="사용 여부" variant="outlined" density="compact" hide-details />
          <div class="remote-filter__actions">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          지역 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th>지역명</th>
            <th>지역 구분</th>
            <th>우편번호 기준</th>
            <th>추가 배송비</th>
            <th>사용</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.shippingRemoteAreaZipCode" class="remote-row" @click="openEditDialog(item)">
            <td>
              <div class="font-weight-bold">{{ item.areaNm }}</div>
            </td>
            <td>
              <v-chip size="small" :color="item.regionType === 'JEJU' ? 'info' : 'warning'" label>{{ regionLabel(item.regionType) }}</v-chip>
            </td>
            <td>{{ zipLabel(item) }}</td>
            <td>{{ formatAmount(item.surchargeAmount) }}</td>
            <td>
              <v-chip size="small" :color="item.activeYn === 'Y' ? 'success' : 'grey'" label>{{ activeLabel(item.activeYn) }}</v-chip>
            </td>
            <td>{{ formatDate(item.createdDt) }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="6" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">mdi-map-marker-off-outline</v-icon>
              등록된 도서산간 지역이 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4">
        <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="680" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">{{ formTitle }}</v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="form.regionType" :items="regionOptions.filter(item => item.value)" label="지역 구분 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.areaNm" label="지역명 *" placeholder="예: 울릉군, 제주 전역" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.zipCode" label="단일 우편번호" placeholder="예: 40200" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.zipFrom" label="우편번호 시작" placeholder="범위 사용 시 입력" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.zipTo" label="우편번호 종료" placeholder="범위 사용 시 입력" variant="outlined" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model.number="form.surchargeAmount" type="number" min="0" label="개별 추가 배송비" placeholder="비우면 배송비 정책 기본값" variant="outlined" suffix="원" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="form.activeYn" :items="[{ title: '사용', value: 'Y' }, { title: '미사용', value: 'N' }]" label="사용 여부" variant="outlined" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="saveRemoteArea">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.remote-filter {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 220px 180px auto;
  gap: 12px;
  align-items: center;
}

.remote-filter__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.remote-row {
  cursor: pointer;
}

.remote-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}

@media (max-width: 959px) {
  .remote-filter {
    grid-template-columns: 1fr;
  }

  .remote-filter__actions {
    justify-content: flex-start;
  }
}
</style>
